import api from 'api';
import reportsApi from 'api/reports/reports.api';
import { errorMessageFactory } from 'factories/api';
import { showToast } from 'redux/modules/toasts.actions';
import { getAccountFeatures } from 'redux/selectors/accounts';
import {
  getPinnedProjectIdsSet,
  getPinnedProjectsFetchStatus,
  getPinnedReportEntities,
} from 'redux/selectors/pins';
import { getReportEntities } from 'redux/selectors/superreports';
import type { Thunk } from 'types/redux';
import * as types from './pins.types';
import { pinsXhrModule } from './pins.types';
import { fetchedLightProjects } from './projects.types';

export function togglePinOnProjectId(projectId: number): Thunk<Promise<unknown>> {
  return (dispatch, getState) => {
    const isAlreadyPinned = getPinnedProjectIdsSet(getState()).has(projectId);
    // We'll re-use this to toggle pins back to original state if
    // request fails -- are there better ways of doing this?
    const togglePin = () => {
      dispatch(
        types.toggleProjectPin({
          projectId,
        })
      );
    };

    togglePin();

    if (isAlreadyPinned) {
      return api.unpinProjectId(projectId).catch(togglePin);
    }
    return api.pinProjectId(projectId).catch(togglePin);
  };
}

export function fetchPinnedProjects(): Thunk<Promise<unknown>> {
  return async (dispatch, getState) => {
    if (!getAccountFeatures(getState()).project_dashboard) {
      // Pins only available when project_dashboard feature is available
      return Promise.resolve();
    }

    // If pins are already fetched, don't re-fetch them:
    if (getPinnedProjectsFetchStatus(getState()).hasInitialized) {
      return Promise.resolve();
    }

    const projects = await dispatch(
      pinsXhrModule.wrapFetching(api.getPinnedProjects(), 'projects')
    );
    dispatch(types.fetchedProjectPins({ projects }));
    dispatch(fetchedLightProjects({ projects }));
  };
}

export function fetchPinnedReports(userIds?: Array<number>): Thunk<Promise<void>> {
  return async dispatch => {
    try {
      const reports = await dispatch(
        pinsXhrModule.wrapFetching(reportsApi.getPinnedReports(userIds), 'reports')
      );
      dispatch(
        types.fetchedReportsPins({
          reports: reports.map(r => ({ ...r, pinned: true })),
        })
      );
    } catch (error) {
      dispatch(showToast({ message: errorMessageFactory(error), type: 'danger' }));
    }
  };
}

export function togglePinOnReportId(reportId: number): Thunk<Promise<void>> {
  return async (dispatch, getState) => {
    const state = getState();
    const reportEntity =
      getPinnedReportEntities(state)[reportId] || getReportEntities(state)[reportId];
    const isAlreadyPinned = !!reportEntity.pinned;

    const handleError = (error: Error) => {
      dispatch(types.toggleReportPin(reportEntity));
      dispatch(showToast({ message: errorMessageFactory(error), type: 'danger' }));
    };
    dispatch(
      types.toggleReportPin(
        isAlreadyPinned ? { ...reportEntity, pinned: false } : { ...reportEntity, pinned: true }
      )
    );

    if (isAlreadyPinned) {
      try {
        await dispatch(pinsXhrModule.wrapSaving(reportsApi.unpinReportId(reportId)));
      } catch (error) {
        handleError(error);
      }
    } else {
      try {
        await dispatch(pinsXhrModule.wrapSaving(reportsApi.pinReportId(reportId)));
      } catch (error) {
        handleError(error);
      }
    }
  };
}
